/**
 * Utility functions for handling Swagger data
 */

/**
 * Extract base64 data from various swagger data formats
 * @param {Object|string} swaggerData - The swagger data (object with fileData or FileData, or direct base64)
 * @returns {string|null} - The base64 string or null
 */
const extractBase64Data = (swaggerData) => {
  if (!swaggerData) return null;

  let base64Data = null;

  // Handle object with fileData or FileData property
  if (typeof swaggerData === "object") {
    base64Data = swaggerData.fileData || swaggerData.FileData;
  } else if (typeof swaggerData === "string") {
    base64Data = swaggerData;
  }

  if (!base64Data) return null;

  // Handle data URL format (data:application/json;base64,...)
  if (base64Data.startsWith("data:")) {
    const commaIndex = base64Data.indexOf(",");
    if (commaIndex !== -1) {
      base64Data = base64Data.substring(commaIndex + 1);
    }
  }

  return base64Data;
};

/**
 * Decode base64 to text as UTF-8. atob() alone returns one character per byte, which garbled
 * every non-ASCII character in the swaggers (Greek, curly quotes, emoji such as the warning
 * sign before "Endpoint Deprecation Notice").
 * @param {string} base64Data - Base64 without the data-URL prefix
 * @returns {string} - The decoded text
 */
export const decodeBase64Utf8 = (base64Data) => {
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new TextDecoder("utf-8").decode(bytes);
};

/**
 * Convert base64 swagger data to a blob URL that can be fetched
 * @param {Object|string} swaggerData - The swagger data object or string
 * @returns {string|null} - The blob URL or null if no data
 */
export const createSwaggerBlobUrl = (swaggerData) => {
  const base64Data = extractBase64Data(swaggerData);
  if (!base64Data) return null;

  try {
    // Decode base64 to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob with JSON content type
    const blob = new Blob([bytes], { type: "application/json" });

    // Create and return blob URL
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error creating swagger blob URL:", error);
    return null;
  }
};

/**
 * Parse swagger JSON from base64 data
 * @param {Object|string} swaggerData - The swagger data object or string
 * @returns {Object|null} - The parsed swagger JSON or null if parsing fails
 */
export const parseSwaggerFromBase64 = (swaggerData) => {
  const base64Data = extractBase64Data(swaggerData);
  if (!base64Data) return null;

  try {
    // Decode base64 to a UTF-8 string
    const jsonString = decodeBase64Utf8(base64Data);

    // Parse JSON
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Error parsing swagger from base64:", error);
    return null;
  }
};

/**
 * Clean up blob URL to prevent memory leaks
 * @param {string} blobUrl - The blob URL to revoke
 */
export const revokeSwaggerBlobUrl = (blobUrl) => {
  if (blobUrl && blobUrl.startsWith("blob:")) {
    URL.revokeObjectURL(blobUrl);
  }
};
